/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import { FC, MouseEvent, SyntheticEvent } from 'react'
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone'
import { useField, useForm } from 'react-final-form'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import noop from 'lodash/noop'
import { FieldValidator } from 'final-form'
import { readFile } from 'utils/readFile'
import * as avatarInputClasses from 'components/AvatarInput/styles'
import { ReactComponent as TrashIcon } from 'assets/icons/cross.svg'
import { toast } from 'utils/toast'
import * as classes from './styles'
import { slice } from './helpers'

export const MAX_LOADED_FILES = 10

export type ImageField = {
  file: File
  src: string
  isUploaded: boolean
  initialValue?: string
}

type Options = {
  sliceFromEnd?: boolean
  maxFiles?: number | null
  handleAccepted?: (acceptedFiles: File[]) => Promise<unknown[]>
  handleRejections?: (fileRejections: FileRejection[]) => Promise<unknown[]>
}

type Props = {
  name: string
  className?: string
  label?: string
  subtitle?: string
  options?: Options
  dropzoneOptions?: DropzoneOptions
  renderImage?: (
    file: { file: File; src: string },
    i: number,
    onRemoveClick: (i: number) => (e: SyntheticEvent<HTMLButtonElement>) => void
  ) => JSX.Element | JSX.Element[]
  validate?: FieldValidator<ImageField[]>
  disabled?: boolean
}

export const DropzoneField: FC<Props> = ({
  name,
  className,
  label,
  subtitle,
  options = {},
  dropzoneOptions,
  renderImage,
  validate,
  disabled,
}) => {
  const form = useForm()
  const {
    input: { value },
    meta,
  } = useField<ImageField[]>(name, { validate })

  const { sliceFromEnd, maxFiles = MAX_LOADED_FILES, handleAccepted = noop, handleRejections = noop } = options

  const isSingleFileUpload = maxFiles === 1

  const handleDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    try {
      await Promise.all([handleAccepted(acceptedFiles), handleRejections(fileRejections)])
      form.mutators.setFieldData(name, { error: null })

      if (acceptedFiles?.length) {
        const updatedPreviewList = await Promise.all(
          (slice(acceptedFiles, maxFiles, sliceFromEnd) as File[]).map(async (file) => {
            try {
              const src = await readFile(file)

              return {
                file,
                src,
                isUploaded: false,
              }
            } catch (error) {
              console.error('Cannot read file', error)
              return {
                file,
                src: '',
                isUploaded: false,
              }
            }
          })
        )

        form.change(name, slice([...value, ...updatedPreviewList], maxFiles, sliceFromEnd))
      }
    } catch (error) {
      let errorMessage = 'An error occurred'
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message)
      }
      form.mutators.setFieldData(name, { error: errorMessage })
      toast.error(`Failed to process files: ${errorMessage}`)
    }
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    disabled,
    ...dropzoneOptions,
  })

  const handleRemoveClick = (index: number) => (e: SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    form.change(
      name,
      value.filter((_: ImageField, i: number) => i !== index)
    )
  }

  const isError = meta.data?.error || Boolean(meta.touched && meta.error)
  const error = meta.data?.error || meta.error

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    // This is needed to prevent event bubbling to dropzone component
    // which in case noClick option is not provided will trigger open two times
    e.stopPropagation()
    open()
  }

  const renderContent = () => {
    if (isDragActive) {
      return (
        <>
          <Typography variant="h6" css={classes.dropTitle}>
            Drop here
          </Typography>
          {subtitle && (
            <Typography variant="h6" css={classes.dropSubtitle}>
              {subtitle}
            </Typography>
          )}
        </>
      )
    }

    if (value?.length && isSingleFileUpload) {
      return null
    }

    return (
      <div css={[classes.dropzoneContent, isError && classes.errorColor]}>
        <Button
          css={classes.addButton}
          variant="outlined"
          color="gray"
          type="button"
          data-testid={name + '-button'}
          onClick={handleClick}
          disabled={disabled}
        >
          <AddRoundedIcon />
        </Button>
        <Typography variant="h6" css={classes.dropTitle}>
          Drop your content
        </Typography>
        {subtitle && (
          <Typography variant="h6" css={classes.dropSubtitle}>
            {subtitle}
          </Typography>
        )}
      </div>
    )
  }

  return (
    <div>
      <section {...getRootProps({ className })}>
        <label>
          {!!label && <Typography css={classes.label}>{label}</Typography>}
          <input {...getInputProps({ name })} />
        </label>

        <div>
          <div css={[classes.dropzone, isError && classes.dropzoneError]}>
            {renderContent()}
            {!isDragActive && value?.length ? (
              <ul css={classes.list}>
                {value.map((file: { file: File; src: string }, i: number) => {
                  if (renderImage) {
                    return renderImage(file, i, handleRemoveClick)
                  }

                  return (
                    <li key={i} css={classes.listItem} data-testid={name + '-file-' + i}>
                      <div css={avatarInputClasses.selectedImage}>
                        <img css={avatarInputClasses.attachment} src={file.src} alt="Attached media" />
                        <Typography variant="body2" css={avatarInputClasses.imageTitle}>
                          {file.file.name}
                        </Typography>
                      </div>
                      <IconButton onClick={handleRemoveClick(i)} disabled={disabled}>
                        <TrashIcon css={[avatarInputClasses.removeIcon, avatarInputClasses.imageInput]} />
                      </IconButton>
                    </li>
                  )
                })}
              </ul>
            ) : null}
          </div>
        </div>
      </section>
      {maxFiles && maxFiles > 1 && value?.length === maxFiles && (
        <Typography variant="caption" css={classes.noteCaption}>
          You can add up to {maxFiles} files
        </Typography>
      )}
      {isError && (
        <Typography variant="caption" css={classes.errorCaption}>
          {error}
        </Typography>
      )}
    </div>
  )
}
